import React from "react";
import { Highlighter, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { availableCategories } from "@/components/reader/readingEngine";

// Swatch hues mirror the subtle tints defined in index.css (.rg-hl-*).
export const HL_SWATCH = {
    definitions: "hsl(174 72% 46%)",
    concepts: "hsl(206 70% 55%)",
    exam_relevant: "hsl(38 82% 58%)",
    entities: "hsl(330 62% 64%)",
    keywords: "hsl(215 14% 64%)",
    numbers: "hsl(190 66% 54%)",
    domain: "hsl(150 52% 52%)",
};

export const HighlightControls = ({ hl, className }) => {
    const { data, loading, error, enabled, setEnabled, layers, toggleLayer, reload, retry } = hl;
    const cats = availableCategories(data);
    const activeCount = cats.filter((c) => enabled && layers[c.id]).length;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    data-testid="highlights-control-button"
                    variant="secondary"
                    size="sm"
                    className={cn(
                        "gap-1.5 bg-surface-3/60",
                        enabled && activeCount > 0 && "text-[hsl(var(--primary))]",
                        className
                    )}
                >
                    <Highlighter className="h-4 w-4" />
                    <span className="hidden sm:inline">Highlights</span>
                    {enabled && activeCount > 0 && (
                        <span className="ml-0.5 rounded-full bg-[hsl(var(--primary))]/20 px-1.5 text-[10px] font-medium">{activeCount}</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 border-border bg-surface-1 p-0" data-testid="highlights-popover">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                        <span className="font-sans-display text-sm font-semibold text-t1">AI Smart Highlights</span>
                    </div>
                    <Switch data-testid="highlights-master-switch" checked={enabled} onCheckedChange={setEnabled} />
                </div>

                <div className="max-h-[340px] overflow-y-auto p-2">
                    {loading && (
                        <div className="flex items-center gap-2 px-3 py-6 text-sm text-t3">
                            <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--primary))]" /> Analyzing document…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="px-3 py-4 text-center">
                            <p className="text-sm text-t3">{error}</p>
                            <Button data-testid="highlights-retry-button" size="sm" variant="secondary" className="mt-2 gap-1.5" onClick={retry}>
                                <RefreshCw className="h-3.5 w-3.5" /> Try again
                            </Button>
                        </div>
                    )}
                    {!loading && !error && data && cats.map((cat) => (
                        <div
                            key={cat.id}
                            data-testid={`highlight-layer-${cat.id}`}
                            role="button"
                            tabIndex={enabled ? 0 : -1}
                            onClick={enabled ? () => toggleLayer(cat.id) : undefined}
                            onKeyDown={enabled ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleLayer(cat.id); } } : undefined}
                            className={cn(
                                "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors outline-none",
                                enabled ? "cursor-pointer hover:bg-surface-2 focus-visible:bg-surface-2" : "opacity-50"
                            )}
                        >
                            <span className="h-3.5 w-3.5 shrink-0 rounded-[4px]" style={{ backgroundColor: HL_SWATCH[cat.id], opacity: 0.9 }} />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm text-t1">{cat.label}</span>
                            </span>
                            {cat.count != null && <span className="font-mono-ui text-[11px] text-t4">{cat.count}</span>}
                            <Switch checked={!!(enabled && layers[cat.id])} tabIndex={-1} className="pointer-events-none" />
                        </div>
                    ))}
                    {!loading && !error && data && cats.length === 0 && (
                        <p className="px-3 py-6 text-center text-sm text-t3">No highlights found for this document.</p>
                    )}
                </div>

                {data && !loading && (
                    <div className="border-t border-border px-3 py-2">
                        <button data-testid="highlights-reanalyze-button" onClick={reload} className="flex items-center gap-1.5 text-xs text-t4 hover:text-t2">
                            <RefreshCw className="h-3 w-3" /> Re-analyze
                        </button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
